import { isCourseFree } from "@repo/shared";
import { useTranslation } from "react-i18next";

import { useAvailableCourses } from "~/api/queries/useAvailableCourses";
import { cn } from "~/lib/utils";
import { resolveDirectEnrollHref } from "~/utils/courseLinks";

import { WashiTape } from "../CuadernoVivo/WashiTape";

import type { SupportedLanguages } from "@repo/shared";

type MethodItem = { title: string; desc: string };

/**
 * Cada papel, distinto: inclinación propia, y su cinta con otra variante y de otro lado.
 *
 * Ojo con lo que NO hay acá: desplazamientos verticales por card. Se probaron y el
 * resultado fue peor — subir una card la pegaba a la de arriba de su columna mientras
 * del otro lado se abría un hueco, y el zigzag se leía como error, no como mano humana.
 * El escalonado ahora es de COLUMNA entera (ver abajo), que sí se lee como intención.
 */
const PAPERS = [
  { rot: "-2.6deg", tape: 0, tapeClass: "!left-[18%]" },
  { rot: "1.5deg", tape: 1, tapeClass: "!left-auto !right-[14%]" },
  { rot: "2.2deg", tape: 2, tapeClass: "!left-[52%]" },
  { rot: "-1.1deg", tape: 4, tapeClass: "!left-auto !right-[26%]" },
];

/**
 * "Así se estudia" — replanteo visual 2026-07-31.
 *
 * Antes: los ítems en una sola columna de texto plano y un recuadro punteado chiquito al
 * costado. Se leía como una lista de features, no como "así se siente".
 *
 * Ahora: seis ítems en grilla de dos columnas (una en mobile) que describen lo que trae
 * cada clase, una línea de cierre, y el player en un marco de celular real — el mismo
 * lenguaje que el teléfono del curso destacado — así el placeholder se lee como
 * intencional hasta que llegue el video.
 */
export function MethodSection() {
  const { t } = useTranslation();
  const items = t("landing.launch.method.items", { returnObjects: true }) as MethodItem[];

  return (
    <section className="landing-grain relative bg-landing-white px-5 py-20 sm:px-8">
      <div className="relative z-10 mx-auto max-w-[1100px]">
        <h2 className="font-display max-w-[20ch] text-balance text-[clamp(24px,3.2vw,34px)] font-extrabold leading-tight tracking-tight text-landing-text">
          {t("landing.launch.method.title")}
        </h2>

        <div className="mt-8 grid items-start gap-10 md:grid-cols-[1fr_270px] md:gap-14">
          <div>
            {/* Dos columnas, sin glifo: título en negrita + detalle en muted alcanzan
                para escanear. El check verde quedó reservado para el temario del curso
                gratis — la página lo estaba usando en cinco secciones. */}
            <ul className="grid gap-x-10 gap-y-5 border-l-2 border-landing-border pl-5 sm:grid-cols-2 sm:pl-6">
              {items.map((item) => (
                <li key={item.title}>
                  <b className="block text-[15px] font-semibold leading-snug text-landing-text">
                    {item.title}
                  </b>
                  <small className="mt-0.5 block text-[14px] leading-snug text-landing-muted">
                    {item.desc}
                  </small>
                </li>
              ))}
            </ul>

            {/* La caja destacada: desarrolla el "empezás por el video, lo demás es
                opcional", que como línea suelta no decía nada.

                NO poner acá una promesa de soporte ("escribinos y te contestamos"): no
                hay nadie garantizando respuesta y sería vender algo que el producto no
                da. Lo que va acá tiene que ser una propiedad del curso, verificable. */}
            <div className="mt-7 rounded-[12px] border border-landing-teal/35 bg-landing-teal-light px-5 py-5 sm:px-6 sm:py-6">
              <p className="font-display text-[clamp(18px,2.2vw,21px)] font-extrabold leading-snug text-landing-text">
                {t("landing.launch.method.depth.title")}
              </p>
              <p className="mt-2 max-w-[56ch] text-[14.5px] leading-relaxed text-landing-text/80">
                {t("landing.launch.method.depth.desc")}
              </p>
            </div>
          </div>

          {/* El player, en marco de celular (mismo lenguaje que el curso destacado).
              PLACEHOLDER: reemplazar por el video real cuando esté. */}
          <div className="flex flex-col items-center gap-3 max-md:mt-2">
            <div className="relative w-[min(185px,50vw)] overflow-hidden rounded-[28px] border-[6px] border-[#2c2622] bg-[#1b1613] shadow-landing-xl [aspect-ratio:9/17] md:w-[230px]">
              <span
                aria-hidden="true"
                className="absolute left-1/2 top-2.5 z-10 h-[5px] w-[58px] -translate-x-1/2 rounded-full bg-[#3b332d]"
              />
              <div className="flex h-full w-full items-center justify-center px-4">
                <span className="rounded-[8px] border border-dashed border-[rgba(255,255,255,0.22)] px-3 py-2 text-center text-[12px] leading-snug text-[rgba(255,255,255,0.55)]">
                  {t("landing.launch.method.playerPlaceholder")}
                </span>
              </div>
            </div>
            <span className="landing-hand rotate-[-2deg] text-[16px] text-landing-teal">
              {t("landing.launch.method.shotNote")} ↑
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Lo que esperamos escuchar. Replanteo 2026-07-31 por honestidad: estas frases se
 * vendían como testimonios reales ("Del grupo beta") y no lo son. Ahora el encuadre
 * dice exactamente lo que son —lo que queremos que el alumno diga cuando termine— y
 * la sección cierra invitando a crear la cuenta, que es la acción que buscamos acá.
 */
export function QuotesSection() {
  const { t, i18n } = useTranslation();
  const quotes = t("landing.launch.quotes.items", { returnObjects: true }) as string[];

  // Mismo criterio de "destacado" que FeaturedFreeCourse y el hero: el CTA lleva al
  // alta real del curso gratis, no a un ancla. Si todavía no cargó, cae a #cursos.
  const { data: courses } = useAvailableCourses({ language: i18n.language as SupportedLanguages });
  const featured = courses?.find((c) => c.isFeatured) ?? courses?.find(isCourseFree);
  const ctaHref = featured ? resolveDirectEnrollHref({ slug: featured.slug }) : "#cursos";

  return (
    // Crema: la sección de arriba (método) es blanca y la de abajo (FAQ) también.
    <section className="landing-grain relative bg-landing-cream/70 px-5 py-20 backdrop-blur-[1px] sm:px-8">
      <div className="relative z-10 mx-auto max-w-[1100px]">
        <div className="mx-auto max-w-[680px] text-center">
          <h2 className="font-display text-balance text-[clamp(22px,3vw,30px)] font-extrabold leading-tight tracking-tight text-landing-text">
            {t("landing.launch.quotes.title")}
          </h2>
          <p className="mx-auto mt-3 max-w-[56ch] text-[15px] leading-relaxed text-landing-muted">
            {t("landing.launch.quotes.lead")}
          </p>
        </div>

        {/* DOS COLUMNAS REALES, una por lado, con los papeles adentro.

            No es `columns` de CSS aunque lo parezca: con multi-column, los hijos
            `absolute` de una card de la SEGUNDA columna se anclaban a la primera, así
            que la cinta y la comilla de ese papel aparecían flotando a 300px de
            distancia, sobre otra card. Tampoco es una grilla: la grilla alinea las filas
            entre sí y dejaba un escalón muerto debajo del papel más corto de cada fila.

            Con un contenedor por columna, cada lado fluye por su cuenta y el
            posicionamiento absoluto queda dentro de su propia card. En mobile las dos
            columnas se apilan y el orden de lectura sigue siendo 1, 2, 3, 4. */}
        <div className="mx-auto mt-10 grid max-w-[980px] items-start gap-8 md:grid-cols-2 md:gap-x-14">
          {[
            quotes.slice(0, Math.ceil(quotes.length / 2)),
            quotes.slice(Math.ceil(quotes.length / 2)),
          ].map((column, col) => (
            <div
              key={col}
              className={cn(
                "flex flex-col gap-8 md:gap-12",
                // La columna derecha arranca más abajo: escalona el bloque entero y se
                // lee como intención, no como desprolijidad.
                col === 1 && "md:mt-12",
              )}
            >
              {column.map((quote, i) => {
                const paper = PAPERS[(col * Math.ceil(quotes.length / 2) + i) % PAPERS.length];
                return (
                  <blockquote
                    key={i}
                    className="relative m-0 flex flex-col rounded-[3px] bg-landing-paper px-6 pb-7 pt-9 shadow-landing-lg transition-transform duration-200 [transform:rotate(var(--rot))] hover:-translate-y-1 sm:px-7"
                    style={
                      {
                        "--rot": paper.rot,
                        // Renglones de cuaderno. El paso (28px) tiene que ser EXACTO al
                        // line-height del texto o las líneas se despegan de la raya.
                        backgroundImage:
                          "linear-gradient(transparent 0 27px, rgba(112,104,94,.07) 27px 28px)",
                        backgroundSize: "100% 28px",
                      } as React.CSSProperties
                    }
                  >
                    <WashiTape variant={paper.tape} className={paper.tapeClass} />
                    <span
                      aria-hidden="true"
                      className="font-display absolute -top-1 left-4 text-[52px] leading-none text-landing-red opacity-25"
                    >
                      “
                    </span>
                    {/* Recto, no en cursiva: la itálica volvía el bloque una masa gris. */}
                    <p className="font-display relative m-0 mb-5 text-[16.5px] not-italic leading-[28px] text-landing-text">
                      {quote}
                    </p>
                    <span
                      aria-hidden="true"
                      className="mt-auto block h-[3px] w-10 rounded-full bg-landing-teal opacity-70"
                    />
                  </blockquote>
                );
              })}
            </div>
          ))}
        </div>

        {/* El cierre: si te viste ahí, creá la cuenta. */}
        <div className="mx-auto mt-12 max-w-[560px] text-center">
          <p className="font-display text-[clamp(17px,2.2vw,21px)] font-bold leading-snug text-landing-text">
            {t("landing.launch.quotes.closing")}
          </p>
          <a
            href={ctaHref}
            data-cuelume-press
            data-cuelume-release
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-[10px] bg-landing-red px-7 py-4 text-[15.5px] font-bold text-white shadow-landing-red transition-all duration-150 hover:-translate-y-0.5 hover:bg-landing-red-hover hover:shadow-landing-red-hover sm:w-auto"
          >
            {t("landing.launch.quotes.cta")} →
          </a>
          <p className="mt-2.5 text-[13px] text-landing-muted">
            {t("landing.launch.quotes.ctaNote")}
          </p>
        </div>
      </div>
    </section>
  );
}
